import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { exportTransactionsToCSV } from '../../utils/csvExportUtils';
import {
  Typography, Button, Box, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  TextField, Card, CardContent, Menu,
  Divider, Tooltip, Stack, Alert, styled, Collapse, Checkbox
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Rule as RuleIcon,
  InfoOutlined as InfoOutlinedIcon,
  FileDownload as FileDownloadIcon,
  FindReplace as FindReplaceIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import api from '../../services/api';
import SortIcon from '@mui/icons-material/Sort';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ListFooter from '../common/ListFooter';
import { Settings as SettingsIcon } from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import ToolbarButton from '../common/ToolbarButton';
import FilterBar from '../common/FilterBar';
import EmptyState from '../common/EmptyState';
import CategoryChip from '../common/CategoryChip';
import StatusChip from '../common/StatusChip';
import AppDialog, { DialogField } from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import ResponsiveListTable from '../common/ResponsiveListTable';

// Styled components
const TransactionCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: theme.shape.borderRadius * 3,
  overflow: 'visible'
}));

// Larger page sizes now that rows are one line tall
const ROWS_OPTIONS = [25, 50, 100];

// Category dropdowns can hold 30+ entries: cap the menu height so the list
// scrolls internally instead of covering the screen, and keep the font in
// step with the compact bulk-action bar (matches the Rules review card).
const CATEGORY_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: { xs: '50vh', sm: 320 },
      mt: 0.5,
      '& .MuiMenuItem-root': {
        fontSize: '0.875rem',
        minHeight: { xs: 44, sm: 36 }
      }
    }
  }
};

// Shared styling for the inline filter fields (white bg so they sit cleanly on the card)
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

// New component props: onCreateRule, onBulkCategorize
const TransactionList = ({ 
  onCreateRule, 
  hideAddForm = false, 
  simplified = false,
  defaultFilters = {},
  onBulkCategorize = null
}) => {
  const { transactions, categories, accounts, updateTransaction, deleteTransaction, addTransaction, fetchTransactions } = useData();
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [aiMessage, setAiMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [viewShowAdvanced, setViewShowAdvanced] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [viewTransaction, setViewTransaction] = useState(null);
  const [editMetaText, setEditMetaText] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(defaultFilters.startDate || '');
  const [filterEndDate, setFilterEndDate] = useState(defaultFilters.endDate || '');
  const [filterCategory, setFilterCategory] = useState(defaultFilters.category || '');
  const [filterDescription, setFilterDescription] = useState(defaultFilters.description || '');
  const [filterAccount, setFilterAccount] = useState(defaultFilters.account || '');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('txn_sortBy') || 'date');
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem('txn_sortDir') || 'desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = parseInt(localStorage.getItem('txn_rowsPerPage') || '50', 10);
    return ROWS_OPTIONS.includes(stored) ? stored : 50;
  });
  const [showFilters, setShowFilters] = useState(true);
  const [activeFilters, setActiveFilters] = useState(0);
  const [allFiltersDialogOpen, setAllFiltersDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null); // mobile-only sort menu
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [keptTransactionIds, setKeptTransactionIds] = useState({});

  
  // New transaction form
  const [newTransaction, setNewTransaction] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    account: '',
    category: '',
    notes: '',
    balance: '', // New required field
    type: ''     // New optional field
  });
  
  // Format transaction ID for better readability
  const formatTransactionId = (id) => {
    if (!id) return '';
    
    // If the ID is very long (like those shown in your screenshot)
    if (id.length > 20) {
      // Show first 8 chars and last 4 chars with ellipsis in middle
      return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
    }
    return id;
  };
  
  // Count active filters
  useEffect(() => {
    let count = 0;
    if (filterStartDate) count++;
    if (filterEndDate) count++;
    if (filterCategory) count++;
    if (filterDescription) count++;
    if (filterAccount) count++;
    setActiveFilters(count);
  }, [filterStartDate, filterEndDate, filterCategory, filterDescription, filterAccount]);

  // Persist rowsPerPage and sort settings
  useEffect(() => {
    localStorage.setItem('txn_rowsPerPage', String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    localStorage.setItem('txn_sortBy', sortBy);
    localStorage.setItem('txn_sortDir', sortDirection);
  }, [sortBy, sortDirection]);

  // Reset selected transactions when filter criteria change
  useEffect(() => {
    setSelectedTransactions([]);
  }, [filterStartDate, filterEndDate, filterCategory, filterDescription, filterAccount]);
  
  const handleSort = (column) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    setSortBy(column);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  const filtered = transactions.filter(txn => {
    const date = new Date(txn.date); // Assuming txn.date is the transaction date string/object

    // Start Date Check (remains the same)
    const matchStartDate = !filterStartDate || new Date(filterStartDate) <= date;

    // End Date Check (modified)
    let matchEndDate = true; // Default to true (included) if no filterEndDate is provided
    if (filterEndDate) {
        const end = new Date(filterEndDate);
        // Create a date object for the day *after* the filterEndDate
        const nextDay = new Date(end);
        nextDay.setDate(end.getDate() + 1);
        // Set time to the very beginning of that next day (00:00:00.000)
        nextDay.setHours(0, 0, 0, 0);

        // Check if the transaction date is strictly *before* the start of the next day
        matchEndDate = date < nextDay;
    }

    // Combine the checks
    const matchDate = matchStartDate && matchEndDate;
    const matchCategory = !filterCategory || (filterCategory === 'Uncategorized' 
                        ? !txn.category || txn.category === undefined || txn.category === null || txn.category === '' 
                        : txn.category === filterCategory);
    const matchDescription = !filterDescription || txn.description?.toLowerCase().includes(filterDescription.toLowerCase());
    const matchAccount = !filterAccount || txn.account === filterAccount || txn.account?.toLowerCase() === filterAccount.toLowerCase();
    return matchDate && matchCategory && matchDescription && matchAccount;
  });

  const sorted = [...filtered].sort((a, b) => {
    let valA = a[sortBy];
    let valB = b[sortBy];
    
    // Handle date comparison separately
    if (sortBy === 'date') {
      valA = new Date(valA);
      valB = new Date(valB);
    }
    
    // Handle numeric comparison
    if (sortBy === 'amount' || sortBy === 'balance') {
      valA = Number(valA);
      valB = Number(valB);
    }
    
    return sortDirection === 'asc'
      ? valA > valB ? 1 : -1
      : valA < valB ? 1 : -1;
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  // Handle select all transactions on current page
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginated.map(txn => txn._id || txn.id);
      setSelectedTransactions(newSelected);
      return;
    }
    setSelectedTransactions([]);
  };
  
  // Handle select single transaction
  const handleSelectTransaction = (id) => {
    const selectedIndex = selectedTransactions.indexOf(id);
    let newSelected = [];
    
    if (selectedIndex === -1) {
      newSelected = [...selectedTransactions, id];
    } else {
      newSelected = selectedTransactions.filter(txnId => txnId !== id);
    }
    
    setSelectedTransactions(newSelected);
  };
  
  // Handle bulk category assignment
  const handleBulkCategoryAssign = async () => {
    if (!selectedCategory || selectedTransactions.length === 0) return;
    
    try {
      // If external handler is provided, use it
      if (onBulkCategorize) {
        await onBulkCategorize(selectedTransactions, selectedCategory);
      } else {
        // Otherwise handle internally
        const updatePromises = selectedTransactions.map(id => {
          const transaction = transactions.find(t => (t._id || t.id) === id);
          if (transaction) {
            const updatedTransaction = { ...transaction, category: selectedCategory };
            return updateTransaction(updatedTransaction);
          }
          return Promise.resolve();
        });
        
        await Promise.all(updatePromises);
      }
      
      // Clear selection after successful update in both cases
      setSelectedTransactions([]);
    } catch (error) {
      console.error('Error updating transactions:', error);
    }

    setSelectedCategory('');
  };

  // AI-categorize the selected transactions. Same endpoint the Rules page
  // uses; high-confidence matches are applied server-side, the rest are left
  // untouched (this page has no suggestion-review UI — that lives on Rules).
  const handleAiCategorizeSelected = async () => {
    if (selectedTransactions.length === 0) return;
    setAiCategorizing(true);
    setAiMessage(null);
    try {
      const { data } = await api.post('/ai/categorize', { transactionIds: selectedTransactions });
      await fetchTransactions({ silent: true });
      if (data.processed === 0) {
        setAiMessage({ severity: 'info', text: 'The selected transactions are already categorized.' });
      } else if (data.applied === 0) {
        setAiMessage({
          severity: 'info',
          text: data.processed === 1
            ? "Couldn't find a confident match for that transaction, so it's untouched. Assign it yourself and the next run will learn from it."
            : `Couldn't find a confident match for those ${data.processed} transactions, so they're untouched. Assign one yourself and the next run will learn from it.`
        });
      } else {
        setAiMessage({
          severity: 'success',
          text: data.applied === data.processed
            ? (data.applied === 1
                ? 'Done! Your transaction is categorized.'
                : `Done! All ${data.applied} transactions are categorized.`)
            : `Categorized ${data.applied} of ${data.processed} transactions. Review the rest on the Rules page or assign them here.`
        });
        setSelectedTransactions([]);
        setSelectedCategory('');
      }
    } catch (error) {
      setAiMessage({
        severity: 'error',
        text: error.response?.data?.error || 'AI categorization failed'
      });
    } finally {
      setAiCategorizing(false);
    }
  };

  // Short date matching the Overview convention ("Sat, 6 Jun"); adds the year when it differs
  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      const opts = { weekday: 'short', day: 'numeric', month: 'short' };
      if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
      return d.toLocaleDateString('en-NZ', opts);
    } catch (e) {
      return dateString;
    }
  };

  // All Filters dialog helpers
  const openAllFiltersDialog = () => setAllFiltersDialogOpen(true);
  const closeAllFiltersDialog = () => setAllFiltersDialogOpen(false);
  const clearAllFiltersAndCloseDialog = () => {
    clearFilters();
    setAllFiltersDialogOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const getCategoryName = (categoryId) => categories.find(cat => cat.id === categoryId)?.name || 'Uncategorized';
  const getCategoryColor = (categoryId) => categories.find(cat => cat.id === categoryId)?.color || '#999';

  const handleEdit = (transaction) => {
    setCurrentTransaction({ ...transaction });
    // Initialize editable meta text with pretty JSON if available
    let metaText = '';
    try {
      metaText =
        transaction?.meta !== undefined && transaction?.meta !== null
          ? (typeof transaction.meta === 'string'
              ? transaction.meta
              : JSON.stringify(transaction.meta, null, 2))
          : '';
    } catch {
      metaText = String(transaction?.meta ?? '');
    }
    setEditMetaText(metaText);
    // Snapshot for the unsaved-changes guard on backdrop/escape close
    setEditSnapshot(JSON.stringify({ ...transaction }) + '|' + metaText);
    setShowAdvanced(false);
    setEditDialogOpen(true);
  };

  const requestCloseEdit = () => {
    const dirty = JSON.stringify(currentTransaction) + '|' + editMetaText !== editSnapshot;
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setEditDialogOpen(false);
  };

  const handleSave = () => {
    // Validate the balance field is present
    if (!currentTransaction.balance && currentTransaction.balance !== 0) {
      alert('Balance field is required');
      return;
    }

    // Prepare updated object and handle meta JSON parsing if provided
    let updated = { ...currentTransaction };
    if (editMetaText && editMetaText.trim().length > 0) {
      try {
        updated.meta = JSON.parse(editMetaText);
      } catch (e) {
        alert('Meta must be valid JSON');
        return;
      }
    }

    if (updated) updateTransaction(updated);
    setEditDialogOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      deleteTransaction(id);
    }
  };

  const handleBulkDelete = () => {
    if (selectedTransactions.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteDialogOpen(false);
    const ids = [...selectedTransactions];
    for (const id of ids) {
      await deleteTransaction(id);
    }
    setSelectedTransactions([]);
  };

  // Duplicate detection: group transactions with same description, amount, date (ignoring account)
  const findDuplicates = () => {
    const groups = {};
    transactions.forEach(txn => {
      const key = `${(txn.description || '').trim().toLowerCase()}|${txn.amount}|${txn.date || ''}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(txn);
    });
    const dupes = Object.values(groups).filter(g => g.length > 1);
    setDuplicateGroups(dupes);
    // Default: keep the first transaction in each group
    const kept = {};
    dupes.forEach((group, idx) => {
      kept[idx] = group[0]._id || group[0].id;
    });
    setKeptTransactionIds(kept);
    setDuplicatesDialogOpen(true);
  };

  const handleKeepChange = (groupIdx, txnId) => {
    setKeptTransactionIds(prev => ({ ...prev, [groupIdx]: txnId }));
  };

  const confirmRemoveDuplicates = async () => {
    setDuplicatesDialogOpen(false);
    for (let i = 0; i < duplicateGroups.length; i++) {
      const group = duplicateGroups[i];
      const keepId = keptTransactionIds[i];
      for (const txn of group) {
        const txnId = txn._id || txn.id;
        if (txnId !== keepId) {
          await deleteTransaction(txnId);
        }
      }
    }
    setDuplicateGroups([]);
    setKeptTransactionIds({});
  };

  const getAccountName = (accountId) => {
    if (!accountId) return '-';
    const acc = accounts.find(a => a.id === accountId || a.name === accountId);
    return acc ? acc.name : accountId;
  };
  
  // New function to handle creating a rule from transaction
  const handleCreateRule = (transaction) => {
    if (onCreateRule) {
      onCreateRule(transaction);
    }
  };
  
  const handleNewTransactionChange = (e) => {
    const { name, value } = e.target;
    setNewTransaction(prev => ({
      ...prev,
      [name]: name === 'amount' || name === 'balance' ? parseFloat(value) : value
    }));
  };
  
  const handleAddTransaction = () => {
    // Validation
    if (!newTransaction.description || !newTransaction.amount || !newTransaction.date || 
        (newTransaction.balance === '' && newTransaction.balance !== 0)) {
      alert('Please fill out all required fields including balance');
      return false;
    }
    
    // Generate a unique ID
    const id = `txn_${Date.now()}`;
    
    addTransaction({
      ...newTransaction,
      id,
      amount: parseFloat(newTransaction.amount),
      balance: parseFloat(newTransaction.balance)
    });
    
    // Reset form
    setNewTransaction({
      description: '',
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      account: '',
      category: '',
      notes: '',
      balance: '',
      type: ''
    });

    return true;
  };
  
  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterCategory('');
    setFilterDescription('');
    setFilterAccount('');
  };

  const handleExportToCSV = () => {
    const currentFilters = {
      startDate: filterStartDate,
      endDate: filterEndDate,
      category: filterCategory,
      description: filterDescription,
      account: filterAccount
    };

    // Prefer exporting selected transactions if any are selected
    const rowsToExport = selectedTransactions.length > 0
      ? filtered.filter(t => selectedTransactions.includes(t._id || t.id))
      : filtered;

    exportTransactionsToCSV(rowsToExport, categories, currentFilters);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <PageHeader title="Transactions" icon={<ListAltIcon fontSize="large" />} />
      {/* Overflow guard sits below the header so it can't clip the
          full-bleed negative margins */}
      <Box sx={{ overflow: 'hidden' }}>
      
  {/* Add New Transaction section removed as requested */}

      {/* Filter Card */}
      <TransactionCard sx={{ overflow: 'hidden', borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header with buttons on left, filters on right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1.5, gap: 2 }}>
            {/* Buttons on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <ToolbarButton startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
                Add
              </ToolbarButton>
              <Tooltip title="Export transactions to CSV">
                <span>
                  <IconButton
                    onClick={handleExportToCSV}
                    disabled={(selectedTransactions.length || filtered.length) === 0}
                    aria-label="Export transactions to CSV"
                    sx={{
                      color: 'primary.contrastText', bgcolor: 'primary.main', borderRadius: 2, width: 36, height: 36,
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&.Mui-disabled': { bgcolor: 'primary.light', color: 'primary.contrastText', opacity: 0.6 }
                    }}
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Find duplicate transactions">
                <IconButton
                  onClick={findDuplicates}
                  aria-label="Find duplicate transactions"
                  sx={{
                    color: 'primary.contrastText', bgcolor: 'primary.main', borderRadius: 2, width: 36, height: 36,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <FindReplaceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Filters on the right */}
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <FilterBar>

                <Button
                  variant="outlined"
                  onClick={openAllFiltersDialog}
                  startIcon={<SettingsIcon fontSize="small" sx={{ mr: 0 }} />}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    borderColor: activeFilters > 0 ? 'primary.main' : 'grey.300',
                    backgroundColor: activeFilters > 0 ? 'primary.lightest' : 'grey.50',
                    color: activeFilters > 0 ? 'primary.dark' : 'grey.700',
                    borderRadius: 2,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    height: 37, minWidth: 120,
                    '&:hover': {
                      backgroundColor: activeFilters > 0 ? 'primary.lightest' : 'grey.100',
                      borderColor: activeFilters > 0 ? 'primary.dark' : 'grey.400',
                    },
                    '& .MuiButton-startIcon': { marginRight: 0 }
                  }}
                >
                  All filters {activeFilters > 0 && `(${activeFilters})`}
                </Button>

                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  size="small"
                  sx={filterFieldSx}
                  inputProps={{ style: { fontSize: '0.875rem' } }}
                />

                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  size="small"
                  sx={filterFieldSx}
                  inputProps={{ style: { fontSize: '0.875rem' } }}
                />

                <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                  <InputLabel id="filter-category-label" shrink>Category</InputLabel>
                <Select
                  labelId="filter-category-label"
                  id="filter-category-select"
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#999' }} />
                      Uncategorized
                    </Box>
                  </MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.875rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={filterDescription}
                onChange={(e) => setFilterDescription(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  style: { fontSize: '0.875rem' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                  <InputLabel id="filter-account-label" shrink>Account</InputLabel>
                  <Select
                    labelId="filter-account-label"
                    value={filterAccount}
                    label="Account"
                    onChange={(e) => setFilterAccount(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Accounts</MenuItem>
                    {accounts.map(acc => (
                      <MenuItem key={acc.id} value={acc.id} sx={{ fontSize: '0.875rem' }}>{acc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
            </FilterBar>
            </Box>
          </Box>
          
          {/* Transaction list section spacing to match Review */}
              <Box sx={{ mb: 2, px: { xs: 2, sm: 2 }, pt: 0, pb: 3 }}>
        {filtered.length === 0 ? (
            <EmptyState
              icon={<SearchOffIcon />}
              title="No matching transactions"
              description="No transactions match your filters. Try adjusting your criteria or adding new transactions."
              action={
                activeFilters > 0 && (
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Box sx={{ mb: 1, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Row 1: result count (+ selection count) and, on mobile, the sort menu */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 28 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.8125rem', fontWeight: 600 }}
                  >
                    Showing {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                    {selectedTransactions.length > 0 && ` · ${selectedTransactions.length} selected`}
                  </Typography>
                  {/* On mobile the table headers are gone, so sorting lives in a menu */}
                  <Box sx={{ display: { xs: 'block', sm: 'none' }, flexShrink: 0 }}>
                    <Button
                      size="small"
                      startIcon={<SortIcon />}
                      onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                      sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 500 }}
                    >
                      Sort by
                    </Button>
                    <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)} keepMounted>
                      {[
                        ['date', 'Date'], ['amount', 'Amount'], ['description', 'Description'],
                        ['account', 'Account'], ['category', 'Category']
                      ].flatMap(([id, label]) => (
                        ['asc', 'desc'].map(dir => (
                          <MenuItem
                            key={`${id}-${dir}`}
                            sx={{ fontSize: '0.875rem' }}
                            onClick={() => { setSortBy(id); setSortDirection(dir); setPage(0); setSortMenuAnchor(null); }}
                          >
                            {label} {dir === 'asc' ? '↑' : '↓'}
                          </MenuItem>
                        ))
                      ))}
                    </Menu>
                  </Box>
                </Box>

                {/* Row 2: bulk actions, matching the Rules review card. On mobile
                    the category picker + Assign fill the first line and the
                    secondary actions wrap to their own line. */}
                {selectedTransactions.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ flex: { xs: 1, sm: '0 1 auto' }, minWidth: { xs: 0, sm: 200 } }}>
                      <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        displayEmpty
                        MenuProps={CATEGORY_MENU_PROPS}
                        inputProps={{ 'aria-label': 'Category to assign' }}
                        renderValue={(selected) => {
                          const cat = categories.find(c => c.id === selected);
                          if (!cat) {
                            return <Box component="span" sx={{ color: 'text.secondary' }}>Choose category…</Box>;
                          }
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color || '#999', flexShrink: 0 }} />
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cat.name}
                              </Box>
                            </Box>
                          );
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          // The theme forces 0.9rem on inputs; keep the displayed
                          // value the same size as the Assign button next to it.
                          '& .MuiSelect-select': { fontSize: '0.875rem' }
                        }}
                      >
                        {categories.filter(c => c.id !== 'uncategorized').map(cat => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color || '#999', flexShrink: 0 }} />
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cat.name}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!selectedCategory}
                      onClick={handleBulkCategoryAssign}
                      sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.875rem', flexShrink: 0, px: 2 }}
                    >
                      Assign
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexBasis: { xs: '100%', sm: 'auto' } }}>
                      <Tooltip title={`Use AI to categorize the ${selectedTransactions.length} selected transaction(s)`}>
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            disabled={aiCategorizing}
                            startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
                            onClick={handleAiCategorizeSelected}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.875rem' }}
                          >
                            {aiCategorizing ? 'Thinking...' : 'AI Categorize'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.875rem' }}
                      >
                        Delete ({selectedTransactions.length})
                      </Button>
                      <Button
                        size="small"
                        onClick={() => { setSelectedTransactions([]); setSelectedCategory(''); }}
                        sx={{ textTransform: 'none', fontSize: '0.875rem', color: 'text.secondary' }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                )}

                {aiMessage && (
                  <Alert
                    severity={aiMessage.severity}
                    onClose={() => setAiMessage(null)}
                    sx={{ fontSize: '0.875rem', py: 0.5 }}
                  >
                    {aiMessage.text}
                  </Alert>
                )}
              </Box>

              <ResponsiveListTable
                items={paginated}
                getId={(t) => t._id || t.id}
                columns={[
                  { id: 'date', label: 'Date', render: t => formatDate(t.date), sx: { color: 'text.secondary' } },
                  { id: 'description', label: 'Description', render: t => t.description || '-', sx: { fontWeight: 600, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' } },
                  {
                    id: 'category', label: 'Category',
                    render: t => (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <CategoryChip
                          label={getCategoryName(t.category)}
                          color={getCategoryColor(t.category)}
                          sx={{ fontSize: '0.75rem', height: 20 }}
                        />
                        {t.isPending && <StatusChip status="warning" label="Pending" sx={{ fontSize: '0.75rem', height: 20 }} />}
                      </Box>
                    )
                  },
                  { id: 'account', label: 'Account', render: t => getAccountName(t.account), sx: { color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' } },
                  {
                    id: 'amount', label: 'Amount', align: 'right',
                    render: t => (
                      <Box component="span" sx={{ fontWeight: 700, color: t.amount > 0 ? 'success.main' : 'error.main' }}>
                        {t.amount < 0 ? '-' : '+'}{formatCurrency(t.amount)}
                      </Box>
                    )
                  }
                ]}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                selectedIds={selectedTransactions}
                onToggleSelect={handleSelectTransaction}
                onSelectAll={(checked) => handleSelectAllClick({ target: { checked } })}
                actions={[
                  { key: 'view', label: 'View', icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />, onClick: (t) => { setViewTransaction(t); setViewShowAdvanced(false); setViewDialogOpen(true); } },
                  { key: 'edit', label: 'Edit', icon: <EditIcon sx={{ fontSize: 18 }} />, onClick: handleEdit },
                  ...(onCreateRule ? [{ key: 'rule', label: 'Create Rule', icon: <RuleIcon sx={{ fontSize: 18 }} />, onClick: handleCreateRule }] : []),
                  { key: 'delete', label: 'Delete', icon: <DeleteIcon sx={{ fontSize: 18 }} />, onClick: (t) => handleDelete(t._id || t.id), danger: true }
                ]}
                onRowClick={(t) => { setViewTransaction(t); setViewShowAdvanced(false); setViewDialogOpen(true); }}
                mobilePrimary={(t) => t.description || '-'}
                mobileSecondary={(t) => `${formatDate(t.date)} • ${getAccountName(t.account)}`}
                mobileChips={(t) => (
                  <>
                    <CategoryChip
                      label={getCategoryName(t.category)}
                      color={getCategoryColor(t.category)}
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                    {t.isPending && <StatusChip status="warning" label="Pending" sx={{ fontSize: '0.75rem', height: 20 }} />}
                  </>
                )}
                mobileAmount={(t) => (
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap' }} color={t.amount > 0 ? 'success.main' : 'error.main'}>
                    {t.amount < 0 ? '-' : '+'}{formatCurrency(t.amount)}
                  </Typography>
                )}
              />
                <ListFooter
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  totalCount={sorted.length}
                  rowsOptions={ROWS_OPTIONS}
                />
              </>
            )}
          
          </Box>
        </CardContent>
      </TransactionCard>

      {/* All Filters Dialog (match Review Transactions) */}
      <Dialog 
        open={allFiltersDialogOpen} 
        onClose={closeAllFiltersDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            p: { xs: 1, sm: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          px: { xs: 1, sm: 3 }
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Filters
          </Typography>
          <Button onClick={closeAllFiltersDialog} size="small">
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 1, sm: 3 } }}>
          <Stack spacing={3}>
            {/* Date Range Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Date Range
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Box>

            {/* Category Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Category
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="dialog-category-label">Category</InputLabel>
                <Select
                  labelId="dialog-category-label"
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#999' }} />
                      Uncategorized
                    </Box>
                  </MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Search Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Search
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Description"
                  value={filterDescription}
                  onChange={(e) => setFilterDescription(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel id="dialog-filter-account-label" shrink>Account</InputLabel>
                  <Select
                    labelId="dialog-filter-account-label"
                    value={filterAccount}
                    label="Account"
                    onChange={(e) => setFilterAccount(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">All Accounts</MenuItem>
                    {accounts.map(acc => (
                      <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1, sm: 3 }, pb: 3, pt: 2 }}>
          <Button 
            onClick={clearAllFiltersAndCloseDialog}
            sx={{ textTransform: 'none' }}
          >
            Clear all
          </Button>
          <Button
            onClick={closeAllFiltersDialog}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        title={selectedTransactions.length === 1 ? 'Delete Transaction' : `Delete ${selectedTransactions.length} Transactions`}
        message={selectedTransactions.length === 1
          ? 'Are you sure you want to delete this transaction? This action cannot be undone.'
          : `Are you sure you want to delete ${selectedTransactions.length} transactions? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmBulkDelete}
      />

      {/* Duplicates Dialog */}
      <Dialog
        open={duplicatesDialogOpen}
        onClose={() => setDuplicatesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {duplicateGroups.length > 0
              ? `Found ${duplicateGroups.length} Duplicate Group${duplicateGroups.length !== 1 ? 's' : ''}`
              : 'No Duplicates Found'}
          </Typography>
          <IconButton onClick={() => setDuplicatesDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {duplicateGroups.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>All transactions are unique. No duplicates detected.</Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which transaction to keep in each group. The others will be deleted.
            </Typography>
          )}
          {duplicateGroups.map((group, groupIdx) => (
            <Box key={groupIdx} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.875rem' }}>
                Group {groupIdx + 1}: {group[0].description || 'No description'} · {formatCurrency(group[0].amount)} · {formatDate(group[0].date)}
              </Typography>
              <Stack spacing={0.5}>
                {group.map(txn => {
                  const txnId = txn._id || txn.id;
                  const isKept = keptTransactionIds[groupIdx] === txnId;
                  return (
                    <Card
                      key={txnId}
                      variant="outlined"
                      onClick={() => handleKeepChange(groupIdx, txnId)}
                      sx={{
                        cursor: 'pointer',
                        borderColor: isKept ? 'success.main' : 'divider',
                        borderWidth: isKept ? 2 : 1,
                        backgroundColor: isKept ? 'success.lighter' : 'transparent',
                        '&:hover': { borderColor: isKept ? 'success.main' : 'primary.main' }
                      }}
                    >
                      <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Checkbox
                          checked={isKept}
                          onChange={() => handleKeepChange(groupIdx, txnId)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          color="success"
                          sx={{ p: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.875rem' }}>
                              {txn.description || '-'}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={txn.amount > 0 ? 'success.main' : 'error.main'}
                              sx={{ ml: 1, whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                            >
                              {txn.amount < 0 ? '-' : '+'}{formatCurrency(txn.amount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">{formatDate(txn.date)}</Typography>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Typography variant="caption" color="text.secondary">{getAccountName(txn.account)}</Typography>
                            <CategoryChip
                              label={getCategoryName(txn.category)}
                              color={getCategoryColor(txn.category)}
                              sx={{ fontSize: '0.75rem', height: 20 }}
                            />
                            {isKept && (
                              <Chip label="Keep" size="small" color="success" sx={{ fontSize: '0.75rem', height: 20, fontWeight: 600 }} />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
              {groupIdx < duplicateGroups.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </DialogContent>
        {duplicateGroups.length > 0 && (
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {duplicateGroups.reduce((sum, g) => sum + g.length - 1, 0)} transaction{duplicateGroups.reduce((sum, g) => sum + g.length - 1, 0) !== 1 ? 's' : ''} will be deleted
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setDuplicatesDialogOpen(false)} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button onClick={confirmRemoveDuplicates} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>
                Remove Duplicates
              </Button>
            </Box>
          </DialogActions>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <AppDialog
        open={editDialogOpen}
        onClose={requestCloseEdit}
        title="Edit Transaction"
        primaryLabel="Save"
        onPrimary={handleSave}
        onSecondary={() => setEditDialogOpen(false)}
      >
        {currentTransaction && (
          <>
            <DialogField label="Date" required>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={currentTransaction.date?.slice(0, 10) || ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, date: e.target.value })}
              />
            </DialogField>

            <DialogField label="Amount" required>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={currentTransaction.amount ?? ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, amount: Number(e.target.value) })}
                InputProps={{ startAdornment: <Box component="span" mr={1}>$</Box> }}
              />
            </DialogField>

            <DialogField label="Description">
              <TextField
                fullWidth
                size="small"
                value={currentTransaction.description || ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, description: e.target.value })}
              />
            </DialogField>

            <DialogField label="Account">
              <FormControl fullWidth size="small">
                <Select
                  value={currentTransaction.account || ''}
                  onChange={(e) => setCurrentTransaction({ ...currentTransaction, account: e.target.value })}
                  displayEmpty
                  renderValue={(value) => (value ? getAccountName(value) : '—')}
                >
                  <MenuItem value="">—</MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                  ))}
                  {currentTransaction.account && !accounts.some(a => a.id === currentTransaction.account) && (
                    <MenuItem value={currentTransaction.account}>{getAccountName(currentTransaction.account)}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </DialogField>

            <DialogField label="Category">
              <FormControl fullWidth size="small">
                <Select
                  value={currentTransaction.category || ''}
                  onChange={(e) => setCurrentTransaction({ ...currentTransaction, category: e.target.value })}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return 'Uncategorized';
                    const cat = categories.find(c => c.id === value);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat?.color || '#999' }} />
                        {cat?.name || 'Uncategorized'}
                      </Box>
                    );
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#999' }} />
                      Uncategorized
                    </Box>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogField>

            <DialogField label="Balance" required helper="The account balance after this transaction.">
              <TextField
                fullWidth
                size="small"
                required
                type="number"
                value={currentTransaction.balance ?? ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, balance: parseFloat(e.target.value) })}
                InputProps={{ startAdornment: <Box component="span" mr={1}>$</Box> }}
                error={!currentTransaction.balance && currentTransaction.balance !== 0}
              />
            </DialogField>

            <DialogField label="Type">
              <TextField
                fullWidth
                size="small"
                value={currentTransaction.type || ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, type: e.target.value })}
                placeholder="Optional"
              />
            </DialogField>

            <DialogField label="Notes">
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={currentTransaction.notes || ''}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, notes: e.target.value })}
                placeholder="Add any additional notes..."
              />
            </DialogField>

            {/* Internal fields tucked away — most edits never need these */}
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setShowAdvanced(s => !s)} sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {showAdvanced ? 'Hide advanced' : 'Show advanced'}
              </Button>
              <Collapse in={showAdvanced}>
                <DialogField label="ID">
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {currentTransaction._id || currentTransaction.id || '—'}
                  </Typography>
                </DialogField>
                <DialogField label="Meta">
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    value={editMetaText}
                    onChange={(e) => setEditMetaText(e.target.value)}
                    placeholder="Raw JSON metadata"
                  />
                </DialogField>
              </Collapse>
            </Box>
          </>
        )}
      </AppDialog>

      {/* View Transaction Dialog */}
      <AppDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        title="Transaction Details"
      >
        {viewTransaction && (
          <>
            <DialogField label="Date">
              <Typography variant="body2">{formatDate(viewTransaction.date)}</Typography>
            </DialogField>
            <DialogField label="Amount">
              <Typography variant="body2" fontWeight={600} color={viewTransaction.amount > 0 ? 'success.main' : 'error.main'}>
                {viewTransaction.amount < 0 ? '-' : '+'}{formatCurrency(viewTransaction.amount || 0)}
              </Typography>
            </DialogField>
            <DialogField label="Balance">
              <Typography variant="body2">
                {(viewTransaction.balance ?? 0) < 0 ? '-' : ''}{formatCurrency(viewTransaction.balance || 0)}
              </Typography>
            </DialogField>
            <DialogField label="Category">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: getCategoryColor(viewTransaction.category) }} />
                <Typography variant="body2">{getCategoryName(viewTransaction.category)}</Typography>
              </Box>
            </DialogField>
            <DialogField label="Account">
              <Typography variant="body2">{getAccountName(viewTransaction.account)}</Typography>
            </DialogField>
            <DialogField label="Description">
              <Typography variant="body2">{viewTransaction.description || '—'}</Typography>
            </DialogField>
            <DialogField label="Type">
              <Typography variant="body2">{viewTransaction.type || '—'}</Typography>
            </DialogField>
            <DialogField label="Notes">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewTransaction.notes || '—'}</Typography>
            </DialogField>

            {/* Internal fields tucked away — most views never need these */}
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setViewShowAdvanced(s => !s)} sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {viewShowAdvanced ? 'Hide advanced' : 'Show advanced'}
              </Button>
              <Collapse in={viewShowAdvanced}>
                <DialogField label="ID">
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {viewTransaction._id || viewTransaction.id || '—'}
                  </Typography>
                </DialogField>
                {viewTransaction.meta && (
                  <DialogField label="Meta">
                    <Typography
                      component="pre"
                      variant="body2"
                      color="text.secondary"
                      sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.8125rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {(() => { try { return JSON.stringify(viewTransaction.meta, null, 2); } catch { return String(viewTransaction.meta); } })()}
                    </Typography>
                  </DialogField>
                )}
              </Collapse>
            </Box>
          </>
        )}
      </AppDialog>

      {/* Add Transaction Dialog */}
      <AppDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title="Add Transaction"
        subtitle="Manually record a transaction"
        primaryLabel="Add"
        onPrimary={() => {
          const ok = handleAddTransaction();
          if (ok) setAddDialogOpen(false);
        }}
      >
        {/* Date + Amount share a row. */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DialogField label="Date" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField fullWidth size="small" type="date" name="date" value={newTransaction.date} onChange={handleNewTransactionChange} />
          </DialogField>
          <DialogField label="Amount" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              name="amount"
              value={newTransaction.amount}
              onChange={handleNewTransactionChange}
              InputProps={{ startAdornment: <Box component="span" mr={1}>$</Box> }}
            />
          </DialogField>
        </Box>
        <DialogField label="Description" required>
          <TextField fullWidth size="small" name="description" value={newTransaction.description} onChange={handleNewTransactionChange} placeholder="e.g. Countdown Henderson" />
        </DialogField>
        {/* Account + Category share a row — where it came from and what kind. */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DialogField label="Account" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField fullWidth size="small" name="account" value={newTransaction.account} onChange={handleNewTransactionChange} />
          </DialogField>
          <DialogField label="Category" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <Select
                name="category"
                value={newTransaction.category}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, category: e.target.value }))}
                displayEmpty
                renderValue={(value) => {
                  if (!value) return 'Uncategorized';
                  const cat = categories.find(c => c.id === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat?.color || '#999', flexShrink: 0 }} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat?.name || 'Uncategorized'}</Box>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#999' }} />
                    Uncategorized
                  </Box>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogField>
        </Box>
        <DialogField label="Balance" required helper="The account balance after this transaction.">
          <TextField
            fullWidth
            size="small"
            required
            type="number"
            name="balance"
            value={newTransaction.balance}
            onChange={handleNewTransactionChange}
            InputProps={{ startAdornment: <Box component="span" mr={1}>$</Box> }}
          />
        </DialogField>
        <DialogField label="Type">
          <TextField fullWidth size="small" name="type" value={newTransaction.type} onChange={handleNewTransactionChange} placeholder="Optional" />
        </DialogField>
        <DialogField label="Notes">
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            name="notes"
            value={newTransaction.notes}
            onChange={handleNewTransactionChange}
            placeholder="Add any additional notes..."
          />
        </DialogField>
      </AppDialog>
      </Box>
    </Box>
  );
};

export default TransactionList;